import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useTenant } from "@/context/TenantContext";
import { PageHeader, Empty } from "@/components/ui-bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { WhatsappLogo, ChatText, EnvelopeSimple, PhoneCall, ShieldCheck, FloppyDisk } from "@phosphor-icons/react";

const CHANNEL_META = {
  whatsapp: { label: "WhatsApp", icon: WhatsappLogo, color: "text-emerald-600" },
  sms: { label: "SMS", icon: ChatText, color: "text-blue-600" },
  email: { label: "Email", icon: EnvelopeSimple, color: "text-violet-600" },
  ivr: { label: "IVR", icon: PhoneCall, color: "text-orange-600" },
};
// Only channels that are actually operational as BYOP integrations are configurable here.
// The rest are surfaced as "Coming Soon" so tenants are never shown non-operational options as available.
const OPERATIONAL = { email: true };
const FIELD_LABEL = {
  api_key: "API Key", api_secret: "API Secret", sender_id: "Sender ID", phone_number_id: "Phone Number ID",
  route: "Route", smtp_host: "SMTP Host", smtp_port: "SMTP Port", smtp_user: "SMTP User",
  smtp_password: "SMTP Password", from_email: "From Email", api_token: "API Token", sid: "SID", caller_id: "Caller ID",
};

export default function Providers() {
  const { isSuper, activeTenant, activeName } = useTenant();
  const [options, setOptions] = useState({ options: {}, fields: {} });
  const [config, setConfig] = useState(null);

  const needsTenant = isSuper && !activeTenant;

  const load = () => {
    api.get("/providers/options").then((r) => setOptions(r.data)).catch(() => {});
    if (needsTenant) { setConfig(null); return; }
    api.get("/providers").then((r) => setConfig(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, [activeTenant]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div data-testid="providers-page">
      <PageHeader title="Advanced Provider Connections" subtitle={isSuper ? `Connect communication provider accounts your business already uses${activeName ? ` — ${activeName}` : ""}` : "Connect communication provider accounts your business already uses. RecoverOS Managed sending is handled for you and isn't configured here."}>
        <span className="inline-flex items-center gap-1.5 text-xs bg-secondary px-2.5 py-1 rounded-sm font-semibold">
          <ShieldCheck size={14} weight="bold" /> Credentials encrypted at rest
        </span>
      </PageHeader>

      {needsTenant ? (
        <Empty text="Select a tenant from the switcher in the top bar to configure its communication providers." />
      ) : !config ? (
        <div className="label-caps">Loading…</div>
      ) : (
        <div className="grid lg:grid-cols-2 gap-4">
          {Object.keys(CHANNEL_META).map((ch) => (
            <ChannelCard
              key={ch}
              channel={ch}
              comingSoon={!OPERATIONAL[ch]}
              providers={options.options[ch] || []}
              fields={options.fields[ch] || []}
              initial={config.channels[ch] || { provider: "", enabled: false, credentials: {} }}
              onSaved={(updated) => setConfig(updated)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function ChannelCard({ channel, comingSoon, providers, fields, initial, onSaved }) {
  const meta = CHANNEL_META[channel];
  const Icon = meta.icon;
  const [provider, setProvider] = useState(initial.provider || "");
  const [enabled, setEnabled] = useState(!!initial.enabled);
  const [creds, setCreds] = useState({ ...initial.credentials });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setCreds((c) => ({ ...c, [k]: v }));

  const save = async () => {
    if (!provider) return toast.error("Select a provider first");
    setSaving(true);
    try {
      // Only send credential fields the user actually has values for (masked values are kept server-side)
      const r = await api.put(`/providers/${channel}`, { provider, enabled, credentials: creds });
      onSaved(r.data);
      toast.success(`${meta.label} provider saved`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  if (comingSoon) {
    return (
      <div className="border border-border bg-card p-5 opacity-95" data-testid={`provider-card-${channel}`}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-sm bg-secondary flex items-center justify-center">
              <Icon size={22} weight="bold" className={meta.color} />
            </div>
            <div>
              <div className="font-heading font-bold text-lg leading-none">{meta.label}</div>
              <div className="text-xs text-muted-foreground mt-1">Not yet available</div>
            </div>
          </div>
          <span className="inline-flex items-center text-[11px] font-semibold bg-secondary text-muted-foreground px-2 py-1 rounded-sm" data-testid={`provider-coming-soon-${channel}`}>Coming Soon</span>
        </div>
        <p className="text-xs text-muted-foreground">Connecting your own {meta.label} provider account will be available here soon.</p>
      </div>
    );
  }

  return (
    <div className="border border-border bg-card p-5" data-testid={`provider-card-${channel}`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-sm bg-secondary flex items-center justify-center">
            <Icon size={22} weight="bold" className={meta.color} />
          </div>
          <div>
            <div className="font-heading font-bold text-lg leading-none">{meta.label}</div>
            <div className="text-xs text-muted-foreground mt-1">{initial.configured ? "Configured" : "Not configured"}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Label className="text-xs">Enabled</Label>
          <Switch checked={enabled} onCheckedChange={setEnabled} data-testid={`provider-enabled-${channel}`} />
        </div>
      </div>

      <div className="space-y-3">
        <div>
          <Label className="text-xs">Provider</Label>
          <Select value={provider || undefined} onValueChange={setProvider}>
            <SelectTrigger className="rounded-sm" data-testid={`provider-select-${channel}`}><SelectValue placeholder="Select provider" /></SelectTrigger>
            <SelectContent>{providers.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {fields.map((f) => (
            <div key={f}>
              <Label className="text-xs">{FIELD_LABEL[f] || f}</Label>
              <Input
                className="rounded-sm mono"
                type={f.includes("password") || f.includes("secret") ? "password" : "text"}
                value={creds[f] ?? ""}
                placeholder={initial.credentials?.[f] || ""}
                onChange={(e) => set(f, e.target.value)}
                data-testid={`provider-${channel}-${f}`}
              />
            </div>
          ))}
        </div>
        <Button onClick={save} disabled={saving} className="rounded-sm gap-2 w-full" data-testid={`provider-save-${channel}`}>
          <FloppyDisk size={16} weight="bold" /> {saving ? "Saving…" : "Save Configuration"}
        </Button>
        <p className="text-[11px] text-muted-foreground">Masked values (••••) are kept secure on the server. Leave a field blank to keep it unchanged.</p>
      </div>
    </div>
  );
}
